import React, { useState, useEffect, useContext } from 'react';

import {
  Grid,
  TextField,
  InputAdornment,
  styled,
  Typography,
} from '@mui/material';
import { Search, Link } from '@mui/icons-material';

import ComponentFactory from '../../../ProjectCanvas/ComponentRegistry';
import useProjectStore from '../../../ProjectCanvas/useProjectStore';
import { StyledTray, StyledTrayContent } from './StyledTray';
import TrayHeader from './TrayHeader';
import {
  ActionTypeGroup,
  isHumanAction,
  isWorkflowOperation,
  isTableOperation,
  isBoardOperation,
  AIIconSVG,
  getCategoryIcon,
} from './CategoryComponents';
import SidebarItem from './SidebarItem';
import { CanvasContext } from '../WorkflowCanvas';
import { AI_AGENTS_CLIENT_IDS } from '../../../../constants/featureFlags';
import { isRemovedComponentType } from '../../../../utils/removedComponentTypes';

const ConnectedToNoticeContainer = styled(Grid)(({ theme }) => ({
  display: 'flex',
  margin: '8px 8px 0 8px',
  padding: '8px 12px',
  alignItems: 'center',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(251, 192, 45, 0.12)' : '#FFFDE7',
  borderLeft: '4px solid #FBC02D',
  borderRadius: '4px',
}));

/** V1 / deprecated types — not addable from the palette; existing canvas nodes still render. */
const PALETTE_HIDDEN = new Set([
  'api_export',
  'calculator',
  'conditional_logic',
  'parse_json',
  'list_transform',
  'conditional_workflow_path',
  'trigger_from_a_workflow',
  'trigger_another_workflow',
  'api_request_and_response',
  'slack',
  'session_variables',
  'webhook',
  'conditional_workflow',
  'webhook_trigger',
  'form',
  'sequential_form',
  'form_section',
  'document_editor',
  'bulk_assessment',
  'create_database_record',
  'query_database_record',
  'update_database_record',
  'trigger_on_database_update',
]);

const isAiAgentAllowed = clientId => {
  const numericId = Number(clientId);
  if (Number.isNaN(numericId)) return false;
  return AI_AGENTS_CLIENT_IDS.includes(numericId);
};

function ComponentTray({ classes, reactFlowInstance: propReactFlowInstance, workflowId, onClose }) {
  const { pendingConnectionSource: pendingConnectionSourceContext } = useContext(CanvasContext);
  const [pendingConnectionSource, setPendingConnectionSource] =
    pendingConnectionSourceContext || useState(null);

  const [searchValue, setSearchValue] = useState('');
  const [displayData, setDisplayData] = useState([]);
  const [defaultComponents, setDefaultComponents] = useState([]);

  const [expandedCategories, setExpandedCategories] = useState({
    triggers: true,
    codeActions: true,
    aiActions: true,
    humanActions: true,
    tableOperations: true,
    boardOperations: true,
    workflowOperations: true,
  });

  useEffect(() => {
    const clientId = useProjectStore.getState().clientId;
    const allowAiAgent = isAiAgentAllowed(clientId);
    const isV2 = useProjectStore.getState().useBpmnJoinBehavior;
    const filteredData = ComponentFactory.filter(component => {
      if (isRemovedComponentType(component.name)) return false;
      if (PALETTE_HIDDEN.has(component.name)) return false;
      if (!allowAiAgent && component.name === 'ai_agent') return false;
      if (isV2 && component.name === 'branch_paths') return false;
      return true;
    });
    setDisplayData(filteredData);
    setDefaultComponents(filteredData);
  }, [useProjectStore.getState().clientId, useProjectStore.getState().useBpmnJoinBehavior]);

  const handleToggleActionType = groupId => {
    setExpandedCategories(prev => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const handleSearch = event => {
    const word = event.target.value.toLowerCase();
    setSearchValue(word);

    const matchesSearch = comp => {
      const compName = comp.displayName.toLowerCase().trim();
      const compDescription = comp.description.toLowerCase().trim();
      return compName.indexOf(word) !== -1 || compDescription.indexOf(word) !== -1;
    };

    if (word.trim() !== '') {
      const searchComp = {};
      Object.keys(defaultComponents).forEach(a => {
        if (matchesSearch(defaultComponents[a])) searchComp[a] = defaultComponents[a];
      });
      setDisplayData(searchComp);
    } else {
      setDisplayData(defaultComponents);
    }
  };

  const renderItem = (component, id) => (
    <Grid item xs={12} key={id}>
      <SidebarItem
        component={component}
        classes={classes}
        reactFlowInstance={propReactFlowInstance}
        workflowId={workflowId}
      />
    </Grid>
  );

  const triggers = Object.values(displayData)
    .filter(c => !c.hidden && c.isTrigger)
    .map(renderItem);

  const aiActions = Object.values(displayData)
    .filter(
      c =>
        !c.hidden &&
        !c.isTrigger &&
        (c.name.includes('ai_') || c.isAIAction) &&
        !isWorkflowOperation(c) &&
        !isTableOperation(c)
    )
    .map(renderItem);

  const humanActions = Object.values(displayData)
    .filter(
      c =>
        !c.hidden &&
        !c.isTrigger &&
        isHumanAction(c) &&
        !isWorkflowOperation(c) &&
        !isTableOperation(c)
    )
    .map(renderItem);

  const codeActions = Object.values(displayData)
    .filter(
      c =>
        !c.hidden &&
        !c.isTrigger &&
        !c.name.includes('ai_') &&
        !isHumanAction(c) &&
        !isWorkflowOperation(c) &&
        !isTableOperation(c) &&
        !isBoardOperation(c)
    )
    .map(renderItem);

  const tableOperations = Object.values(displayData)
    .filter(c => !c.hidden && !c.isTrigger && isTableOperation(c))
    .map(renderItem);

  const boardOperations = Object.values(displayData)
    .filter(c => !c.hidden && !c.isTrigger && isBoardOperation(c))
    .map(renderItem);

  const workflowOperations = Object.values(displayData)
    .filter(c => !c.hidden && !c.isTrigger && isWorkflowOperation(c))
    .map(renderItem);

  return (
    <StyledTray>
      <TrayHeader title="Components" onClose={onClose} />
      <StyledTrayContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              className={classes.search}
              placeholder="Search for component"
              value={searchValue}
              id="components-search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              onChange={handleSearch}
              variant="outlined"
              size="small"
              sx={{ marginBottom: '4px' }}
            />
          </Grid>

          {pendingConnectionSource && (
            <ConnectedToNoticeContainer item xs={12}>
              <Link fontSize="small" sx={{ color: '#FBC02D', marginRight: '8px' }} />{' '}
              <Typography variant="caption" sx={{ color: '#5D4037' }}>
                {' '}
                Adding component connected to "
                {pendingConnectionSource.displayName || 'previous component'}"
              </Typography>
            </ConnectedToNoticeContainer>
          )}

          <Grid item xs={12}>
            <Grid container spacing={1} className={classes.container}>
              <AIIconSVG />

              {triggers.length > 0 && (
                <Grid item xs={12} id="triggers-group">
                  <ActionTypeGroup
                    title="Triggers"
                    icon={getCategoryIcon('triggers')}
                    groupId="triggers"
                    components={triggers}
                    isExpanded={expandedCategories.triggers}
                    onToggle={handleToggleActionType}
                  />
                </Grid>
              )}
              {aiActions.length > 0 && (
                <Grid item xs={12} id="ai-actions-group">
                  <ActionTypeGroup
                    title="AI"
                    icon={getCategoryIcon('aiActions')}
                    groupId="aiActions"
                    components={aiActions}
                    isExpanded={expandedCategories.aiActions}
                    onToggle={handleToggleActionType}
                  />
                </Grid>
              )}
              {humanActions.length > 0 && (
                <Grid item xs={12} id="human-actions-group">
                  <ActionTypeGroup
                    title="Human-in-the-loop"
                    icon={getCategoryIcon('humanActions')}
                    groupId="humanActions"
                    components={humanActions}
                    isExpanded={expandedCategories.humanActions}
                    onToggle={handleToggleActionType}
                  />
                </Grid>
              )}
              {codeActions.length > 0 && (
                <Grid item xs={12} id="code-actions-group">
                  <ActionTypeGroup
                    title="Code"
                    icon={getCategoryIcon('codeActions')}
                    groupId="codeActions"
                    components={codeActions}
                    isExpanded={expandedCategories.codeActions}
                    onToggle={handleToggleActionType}
                  />
                </Grid>
              )}
              {tableOperations.length > 0 && (
                <Grid item xs={12} id="table-operations-group">
                  <ActionTypeGroup
                    title="Table Operations"
                    icon={getCategoryIcon('tableOperations')}
                    groupId="tableOperations"
                    components={tableOperations}
                    isExpanded={expandedCategories.tableOperations}
                    onToggle={handleToggleActionType}
                  />
                </Grid>
              )}
              {boardOperations.length > 0 && (
                <Grid item xs={12} id="board-operations-group">
                  <ActionTypeGroup
                    title="Board Operations"
                    icon={getCategoryIcon('boardOperations')}
                    groupId="boardOperations"
                    components={boardOperations}
                    isExpanded={expandedCategories.boardOperations}
                    onToggle={handleToggleActionType}
                  />
                </Grid>
              )}
              {workflowOperations.length > 0 && (
                <Grid item xs={12} id="workflow-operations-group">
                  <ActionTypeGroup
                    title="Workflow Operations"
                    icon={getCategoryIcon('workflowOperations')}
                    groupId="workflowOperations"
                    components={workflowOperations}
                    isExpanded={expandedCategories.workflowOperations}
                    onToggle={handleToggleActionType}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </StyledTrayContent>
    </StyledTray>
  );
}

export default ComponentTray;
